import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from 'components/Modal';
import { Step } from 'components/SteppedModal';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import {
  youtubeCategoriesSelector,
  youtubeLanguagesListSelector,
} from 'redux/modules/social';
import { YouTubeSocialShareStep } from '../../types';
import { AdditionalDetailsFormState } from './types';
import YouTubeAdditionalDetailsStep, {
  YouTubeAdditionalDetailsStepProps,
} from './YouTubeAdditionalDetailsStep';

export type UseYouTubeAdditionalDetailsStepConfigProps = Pick<
  YouTubeAdditionalDetailsStepProps,
  | 'visibility'
  | 'madeForKids'
  | 'categoryId'
  | 'language'
  | 'tags'
  | 'onVisibilityChange'
  | 'onMadeForKidsChange'
  | 'onCategoryIdChange'
  | 'onLanguageChange'
  | 'onTagsChange'
  | 'resetComposerValues'
> & {
  isActive: boolean;
};

export default function useYouTubeAdditionalDetailsStep({
  visibility,
  madeForKids,
  categoryId,
  language,
  tags,
  isActive,
  onVisibilityChange,
  onMadeForKidsChange,
  onCategoryIdChange,
  onLanguageChange,
  onTagsChange,
  resetComposerValues,
}): Step<YouTubeSocialShareStep> {
  const [prevActiveState, setPrevActiveState] = useState<boolean>(isActive);
  const [draftState, setDraftState] = useState<AdditionalDetailsFormState>({
    visibility,
    madeForKids,
    categoryId,
    language,
    tags,
  });

  const categories = useSelector(youtubeCategoriesSelector);
  const languages = useSelector(youtubeLanguagesListSelector);

  const { goToStep } = useSocialShareModal();

  const handleUpdateValue = <TDataKey extends keyof AdditionalDetailsFormState>(
    key: TDataKey,
  ) => (value: AdditionalDetailsFormState[TDataKey]): void => {
    setDraftState(currDraftState => ({
      ...currDraftState,
      [key]: value,
    }));
  };

  const resetDraftState = useCallback(() => {
    setDraftState({
      visibility,
      madeForKids,
      categoryId,
      language,
      tags,
    });
  }, [categoryId, language, madeForKids, tags, visibility]);

  const handleGoBack = useCallback(() => {
    resetDraftState();
    goToStep('composer');
  }, [goToStep, resetDraftState]);

  const handleSubmit = useCallback(() => {
    onVisibilityChange(draftState.visibility);
    onMadeForKidsChange(draftState.madeForKids);
    onCategoryIdChange(draftState.categoryId);
    onLanguageChange(draftState.language);
    onTagsChange(draftState.tags);
    goToStep('composer');
  }, [
    draftState.categoryId,
    draftState.language,
    draftState.madeForKids,
    draftState.tags,
    draftState.visibility,
    goToStep,
    onCategoryIdChange,
    onLanguageChange,
    onMadeForKidsChange,
    onTagsChange,
    onVisibilityChange,
  ]);

  if (prevActiveState !== isActive) {
    resetDraftState();
    setPrevActiveState(isActive);
  }

  return {
    id: 'additional-details',
    component: (
      <YouTubeAdditionalDetailsStep
        {...draftState}
        {...{ categories, languages, resetComposerValues }}
        onVisibilityChange={handleUpdateValue('visibility')}
        onMadeForKidsChange={handleUpdateValue('madeForKids')}
        onCategoryIdChange={handleUpdateValue('categoryId')}
        onLanguageChange={handleUpdateValue('language')}
        onTagsChange={handleUpdateValue('tags')}
        onClickBack={handleGoBack}
      />
    ),
    fluidFooterButtons: true,
    renderFooterButtons: () => [
      <Modal.FooterButton size="large" theme="submit" onClick={handleSubmit}>
        save changes
      </Modal.FooterButton>,
    ],
  };
}
